import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import CTABanner from '@/components/feature/CTABanner';
import FAQAccordion from '@/components/base/FAQAccordion';
import { useSEO } from '@/hooks/useSEO';

const etapas = [
  { icon: 'ri-search-eye-line', title: 'Briefing e Diagnóstico', desc: 'Entendemos suas necessidades, cultura da empresa, número de colaboradores, fluxo de trabalho e identidade visual. Visitamos o espaço e levantamos todas as medidas.' },
  { icon: 'ri-pencil-ruler-2-line', title: 'Projeto e Planejamento', desc: 'Nossa equipe desenvolve o layout completo do espaço com planta baixa, especificação de materiais, mobiliário, divisórias, iluminação e acabamentos.' },
  { icon: 'ri-hammer-line', title: 'Execução da Obra', desc: 'Coordenamos toda a execução: marcenaria, divisórias, elétrica, pintura, piso e instalações. Você não precisa gerenciar nenhum fornecedor.' },
  { icon: 'ri-sofa-line', title: 'Fornecimento do Mobiliário', desc: 'Fornecemos e instalamos todo o mobiliário corporativo — cadeiras, mesas, armários, cabines acústicas e acessórios — das melhores marcas do mercado.' },
  { icon: 'ri-check-double-line', title: 'Entrega Chave na Mão', desc: 'Entregamos o espaço 100% pronto para uso. Você chega, liga o computador e começa a trabalhar. Sem pendências, sem retrabalho.' },
];

const diferenciais = [
  { icon: 'ri-user-settings-line', title: 'Gestão Única', desc: 'Um único ponto de contato para toda a obra. Sem precisar coordenar múltiplos fornecedores.' },
  { icon: 'ri-time-line', title: 'Prazo Garantido', desc: 'Cronograma definido desde o início, com entregas parciais e comunicação constante.' },
  { icon: 'ri-shield-check-line', title: 'Qualidade Certificada', desc: 'Materiais e marcas de primeira linha, com garantia em todos os itens fornecidos.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Orçamento Fechado', desc: 'Sem surpresas no final. O preço acordado é o preço pago, com escopo bem definido.' },
  { icon: 'ri-building-2-line', title: 'Experiência Corporativa', desc: 'Mais de 10 anos equipando escritórios, clínicas, escolas e espaços públicos em todo o Nordeste.' },
  { icon: 'ri-map-pin-line', title: 'Atendimento Regional', desc: 'Atendemos Caruaru, Recife, Fortaleza e todo o Nordeste com equipe própria de instalação.' },
];

const segmentos = [
  { icon: 'ri-building-2-line', label: 'Escritórios Corporativos' },
  { icon: 'ri-hospital-line', label: 'Clínicas e Hospitais' },
  { icon: 'ri-school-line', label: 'Escolas e Universidades' },
  { icon: 'ri-government-line', label: 'Órgãos Públicos' },
  { icon: 'ri-store-2-line', label: 'Coworkings' },
  { icon: 'ri-restaurant-line', label: 'Restaurantes e Food Service' },
];

const cases = [
  {
    title: 'Escritório Corporativo — Recife, PE',
    area: '420m²',
    prazo: '45 dias',
    desc: 'Projeto completo com divisórias de vidro, marcenaria planejada, 80 estações de trabalho e sala de reunião com cabine acústica.',
    img: 'https://readdy.ai/api/search-image?query=modern%20corporate%20office%20interior%20complete%20renovation%20open%20space%20workstations%20glass%20partitions%20professional%20elegant%20minimalist%20design%20warm%20lighting%20Recife%20Brazil&width=1200&height=800&seq=tk01&orientation=landscape',
  },
  {
    title: 'Clínica Médica — Caruaru, PE',
    area: '180m²',
    prazo: '30 dias',
    desc: 'Reforma completa com divisórias sanitárias Pertech, recepção planejada, consultórios e sala de espera com mobiliário premium.',
    img: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20interior%20complete%20renovation%20reception%20area%20waiting%20room%20clean%20white%20minimalist%20professional%20healthcare%20design%20warm%20lighting%20Brazil&width=1200&height=800&seq=tk02&orientation=landscape',
  },
  {
    title: 'Escola Municipal — Agreste, PE',
    area: '600m²',
    prazo: '60 dias',
    desc: 'Fornecimento e instalação de mobiliário escolar completo para 12 salas de aula, biblioteca e sala dos professores via licitação pública.',
    img: 'https://readdy.ai/api/search-image?query=modern%20school%20classroom%20interior%20complete%20renovation%20student%20desks%20chairs%20library%20bright%20educational%20environment%20professional%20Brazil&width=1200&height=800&seq=tk03&orientation=landscape',
  },
];

const faqs = [
  { q: 'O que está incluído no serviço Turnkey?', a: 'Projeto arquitetônico, execução de obra (marcenaria, divisórias, elétrica, pintura, piso), fornecimento e instalação de todo o mobiliário corporativo. Entregamos o espaço 100% pronto para uso.' },
  { q: 'Como funciona o orçamento?', a: 'Fazemos uma visita técnica gratuita, levantamos todas as necessidades e apresentamos um orçamento fechado com escopo detalhado. Sem surpresas no final.' },
  { q: 'Qual o prazo médio de entrega?', a: 'Depende do tamanho do projeto. Projetos menores (até 200m²) costumam ser entregues em 30 a 45 dias. Projetos maiores podem levar de 60 a 90 dias.' },
  { q: 'Vocês atendem fora de Caruaru?', a: 'Sim. Atendemos Recife, Fortaleza, João Pessoa e todo o Nordeste com equipe própria de projeto e instalação.' },
  { q: 'Atendem licitações públicas com Turnkey?', a: 'Sim. Temos experiência em projetos para órgãos públicos, prefeituras, câmaras e escolas municipais, com toda a documentação necessária para licitação.' },
  { q: 'Há garantia nos serviços e produtos?', a: 'Sim. Todos os produtos fornecidos têm garantia de fábrica e os serviços de instalação têm garantia de 12 meses contra defeitos de execução.' },
];

export default function TurnkeyPage() {
  useSEO({
    title: 'Turnkey em Caruaru PE | Obras Corporativas Completas',
    description: 'Serviço Turnkey em Caruaru PE: do projeto ao mobiliário, entregamos seu espaço corporativo 100% pronto para uso. Escritórios, clínicas, escolas e órgãos públicos em Caruaru, Recife e todo o Nordeste.',
    keywords: 'turnkey Caruaru, obra corporativa Pernambuco, projeto escritório completo Recife, reforma corporativa Agreste, chave na mão Nordeste, turnkey escritório PE',
    path: '/turnkey',
    ogTitle: 'Turnkey em Caruaru PE | Obras Corporativas Completas',
    ogDescription: 'Do projeto ao mobiliário, entregamos seu espaço corporativo 100% pronto para uso em Caruaru, Recife e todo o Nordeste.',
    dateModified: '2026-04-27',
  });

  useEffect(() => {
    const schemaId = 'schema-turnkey';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Turnkey Caruaru — Obras Corporativas Completas',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Home Office Premium',
        url: 'https://homeofficepremium.com',
        telephone: '+55-81-98203-0285',
        email: 'ednilsonleocadio@gmail.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Av. Agamenon Magalhães, 1519',
          addressLocality: 'Caruaru',
          addressRegion: 'PE',
          postalCode: '55014-000',
          addressCountry: 'BR',
        },
        geo: { '@type': 'GeoCoordinates', latitude: -8.2836, longitude: -36.0078 },
      },
      description: 'Serviço Turnkey em Caruaru PE: projeto, execução de obra e fornecimento de mobiliário corporativo. Entregamos o espaço 100% pronto para uso em Caruaru, Recife e todo o Nordeste.',
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/turnkey`,
      areaServed: [
        { '@type': 'City', name: 'Caruaru' },
        { '@type': 'State', name: 'Pernambuco' },
        { '@type': 'City', name: 'Recife' },
        { '@type': 'City', name: 'Fortaleza' },
        { '@type': 'City', name: 'João Pessoa' },
        { '@type': 'State', name: 'Nordeste' },
      ],
      serviceType: 'Obra Corporativa Turnkey',
      category: 'Serviços de Construção e Reforma Corporativa',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Serviços Turnkey',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Projeto Arquitetônico Corporativo' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Execução de Obra Corporativa' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Fornecimento de Mobiliário Corporativo' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Instalação e Montagem Completa' } },
        ],
      },
    });

    const breadcrumbId = 'schema-turnkey-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? '' },
        { '@type': 'ListItem', position: 2, name: 'Turnkey', item: `${import.meta.env.VITE_SITE_URL ?? ''}/turnkey` },
      ],
    });

    const faqSchemaId = 'schema-turnkey-faq';
    let faqScript = document.getElementById(faqSchemaId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqSchemaId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    return () => { script?.remove(); bcScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        eyebrow="Serviço Turnkey"
        eyebrowIcon="ri-key-2-line"
        title={
          <>
            Seu espaço corporativo <span className="text-white/50 font-light">pronto para usar.</span>
          </>
        }
        subtitle="Do projeto ao mobiliário, entregamos obras corporativas completas. Você não gerencia nada — nós cuidamos de tudo e entregamos a chave."
        image="https://readdy.ai/api/search-image?query=luxury%20modern%20corporate%20office%20interior%20complete%20renovation%20project%20management%20construction%20team%20professional%20workspace%20elegant%20minimalist%20design%20warm%20tones%20sophisticated%20atmosphere%20Brazil&width=1600&height=1000&seq=turnkey-hero-2026&orientation=landscape"
        alt="Turnkey — Obras Corporativas Completas"
        ctas={[
          { label: 'Solicitar orçamento', to: '/contato' },
          { label: 'Falar no WhatsApp', href: 'https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es', variant: 'secondary' },
        ]}
      />

      {/* O que é Turnkey */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <SectionHeading
                eyebrow="O que é Turnkey?"
                title="Uma solução, um fornecedor, um resultado perfeito."
                subtitle="Turnkey (ou 'chave na mão') é o modelo onde uma única empresa é responsável por todo o processo — do projeto inicial até a entrega do espaço completamente pronto para uso."
              />
              <p className="mt-6 text-[15px] text-foreground-500 leading-relaxed font-light max-w-xl">
                Você não precisa contratar arquiteto, construtora, marceneiro, fornecedor de móveis e instalador separadamente. A <strong className="font-medium text-foreground-700">Home Office Premium</strong> coordena tudo por você em Caruaru e toda a região de Pernambuco.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {['Projeto arquitetônico', 'Execução de obra', 'Marcenaria', 'Divisórias', 'Mobiliário', 'Elétrica', 'Pintura', 'Entrega final'].map((tag) => (
                  <span key={tag} className="bg-background-50 text-foreground-600 text-[13px] font-medium px-4 py-2 rounded-full border border-background-200/60">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="img-zoom rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=corporate%20office%20interior%20design%20project%20planning%20blueprint%20architectural%20drawing%20modern%20workspace%20renovation%20professional%20team%20meeting%20elegant%20minimalist%20warm%20tones&width=1200&height=900&seq=turnkey-what-2026&orientation=portrait"
                alt="Projeto Turnkey"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Etapas */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading
            eyebrow="Como funciona"
            title="Processo Turnkey."
            subtitle="5 etapas claras, do primeiro contato à entrega das chaves."
            align="center"
            className="mb-14"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {etapas.map((e, i) => (
              <div key={e.title} className="border-t border-background-200/70 pt-6">
                <span className="text-primary-600 font-semibold text-[13px] tracking-wider">0{i + 1}</span>
                <h3 className="text-foreground-900 font-medium text-[15px] mt-2 mb-2">{e.title}</h3>
                <p className="text-foreground-500 text-[13px] leading-relaxed font-light">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Por que escolher" title="Nossos diferenciais." className="mb-12" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {diferenciais.map((d) => (
              <div key={d.title} className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                  <i className={`${d.icon} text-lg`}></i>
                </span>
                <div>
                  <h3 className="text-foreground-900 font-medium text-[15px] mb-1">{d.title}</h3>
                  <p className="text-foreground-500 text-[14px] leading-relaxed font-light">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Segmentos */}
      <section className="bg-foreground-950">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading
            eyebrow="Quem atendemos"
            title={<span className="text-white">Segmentos atendidos.</span>}
            className="mb-12"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {segmentos.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 text-primary-400 mb-3">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <p className="text-white/70 text-[13px] font-light leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Resultados" title="Projetos entregues." className="mb-12" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-12">
            {cases.map((c) => (
              <div key={c.title} className="group">
                <div className="img-zoom rounded-xl overflow-hidden bg-background-100 aspect-[4/3]">
                  <img
                    src={c.img}
                    alt={c.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="pt-4">
                  <h3 className="text-foreground-900 font-medium text-[15px] leading-snug">{c.title}</h3>
                  <p className="text-foreground-400 text-[13px] font-light mt-1">{c.area} · {c.prazo}</p>
                  <p className="text-foreground-500 text-[13px] font-light mt-2 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Perguntas frequentes" title="FAQ — Turnkey." className="mb-12" />
          <div className="max-w-3xl">
            {faqs.map((f) => (
              <FAQAccordion key={f.q} question={f.q} answer={f.a} />
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        eyebrow="Pronto para começar?"
        title="Transforme seu espaço corporativo."
        subtitle="Visita técnica gratuita e projeto personalizado para o seu espaço, sem compromisso."
        primaryLabel="Solicitar visita técnica"
        primaryTo="/contato"
        secondaryLabel="WhatsApp"
        secondaryHref="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
      />

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}